import React, { useEffect } from "react";

import {
  Divider,
  Typography,
  Grid,
  Table,
  TableContainer,
  TableHead,
  TableBody,
  TableCell,
  TableRow,
} from "@mui/material";

import {
  PieChart,
  Pie,
  Cell,
  Legend,
  Tooltip,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  ComposedChart,
  ResponsiveContainer,
  Area,
  ReferenceLine,
} from "recharts";

import moment from "moment";

export default function CharterReport({
  data,
  dataHospitals,
  fromDate,
  toDate,
  ...props
}) {
  useEffect(() => {}, []);

  const generateGenderGraph = (patientList) => {
    let maleCount = 0;
    let femaleCount = 0;
    let unknownCount = 0;

    patientList.forEach((patient) => {
      if (patient.gender === "m") {
        maleCount++;
      } else if (patient.gender === "f") {
        femaleCount++;
      } else if (patient.gender === "u") {
        unknownCount++;
      }
    });

    const COLORS = ["#0088FE", "#FF69B4", "#757575"];

    const totalPatients = maleCount + femaleCount + unknownCount;

    const data = [
      {
        name: "Male",
        value: maleCount,
        percentage: (maleCount / totalPatients) * 100,
      },
      {
        name: "Female",
        value: femaleCount,
        percentage: (femaleCount / totalPatients) * 100,
      },
      {
        name: "Unknown",
        value: unknownCount,
        percentage: (unknownCount / totalPatients) * 100,
      },
    ];

    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          flexDirection: "column",
          width: 350,
          height: 300,
        }}
      >
        <Typography
          variant="subtitle2"
          align="center"
          style={{ whiteSpace: "nowrap" }}
        >
          Bed Usage By Sex
        </Typography>
        <PieChart width={350} height={300}>
          <Pie
            dataKey="value"
            data={data}
            cx={"50%"}
            cy={"50%"}
            outerRadius={80}
            fill="#8884d8"
            label={({
              cx,
              cy,
              midAngle,
              innerRadius,
              outerRadius,
              percent,
              value,
              index,
            }) => {
              const RADIAN = Math.PI / 180;

              const radius = 25 + innerRadius + (outerRadius - innerRadius);
              const x = cx + radius * Math.cos(-midAngle * RADIAN);
              const y = cy + radius * Math.sin(-midAngle * RADIAN);

              return (
                <text
                  x={x}
                  y={y}
                  fill={COLORS[index % COLORS.length]}
                  textAnchor={x > cx ? "start" : "end"}
                  dominantBaseline="central"
                >
                  {data[index].percentage.toFixed(0) +
                    "% (" +
                    data[index].value +
                    ")"}
                </text>
              );
            }}
          >
            {data.map((entry, index) => (
              <Cell
                key={`cell-${index}`}
                fill={COLORS[index % COLORS.length]}
              />
            ))}
          </Pie>
          <Legend
            align="center"
            verticalAlign="bottom"
            height={36}
            payload={data.map((item, index) => ({
              id: item.name,
              type: "square",
              value: `${item.name} - ${item.percentage.toFixed(0)}% (${
                item.value
              })`,
              color: COLORS[index % COLORS.length],
            }))}
          />

          <Tooltip />
        </PieChart>
      </div>
    );
  };

  const generateStatusGraph = (patientList) => {
    let admitted = 0;
    let discharged = 0;

    patientList.forEach((patient) => {
      if (patient.status === 1) {
        discharged++;
      } else if (patient.status === 0) {
        admitted++;
      }
    });

    const COLORS = ["#11cb5f", "#C70039"];

    const totalPatients = admitted + discharged;

    const data = [
      {
        name: "Active",
        value: admitted,
        percentage: (admitted / totalPatients) * 100,
      },
      {
        name: "Discharged",
        value: discharged,
        percentage: (discharged / totalPatients) * 100,
      },
    ];

    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          flexDirection: "column",
          width: 350,
          height: 300,
        }}
      >
        <Typography
          variant="subtitle2"
          align="center"
          style={{ whiteSpace: "nowrap" }}
        >
          Patients Under Care
        </Typography>
        <PieChart width={350} height={300}>
          <Pie
            dataKey="value"
            data={data}
            cx={"50%"}
            cy={"50%"}
            outerRadius={80}
            fill="#8884d8"
            label={({
              cx,
              cy,
              midAngle,
              innerRadius,
              outerRadius,
              percent,
              value,
              index,
            }) => {
              const RADIAN = Math.PI / 180;

              const radius = 25 + innerRadius + (outerRadius - innerRadius);
              const x = cx + radius * Math.cos(-midAngle * RADIAN);
              const y = cy + radius * Math.sin(-midAngle * RADIAN);

              return (
                <text
                  x={x}
                  y={y}
                  fill={COLORS[index % COLORS.length]}
                  textAnchor={x > cx ? "start" : "end"}
                  dominantBaseline="central"
                >
                  {data[index].percentage.toFixed(0) +
                    "% (" +
                    data[index].value +
                    ")"}
                </text>
              );
            }}
          >
            {data.map((entry, index) => (
              <Cell
                key={`cell-${index}`}
                fill={COLORS[index % COLORS.length]}
              />
            ))}
          </Pie>
          <Legend
            align="center"
            verticalAlign="bottom"
            height={36}
            payload={data.map((item, index) => ({
              id: item.name,
              type: "square",
              value: `${item.name} - ${item.percentage.toFixed(0)}% (${
                item.value
              })`,
              color: COLORS[index % COLORS.length],
            }))}
          />
          <Tooltip />
        </PieChart>
      </div>
    );
  };

  const generateEDDMetricsGraph = (patientList) => {
    let edd_met = 0;
    let edd_over = 0;
    let edd_under = 0;

    patientList.forEach((patient) => {
      if (patient.edd_alignment === 0) {
        edd_met++;
      } else if (patient.edd_alignment > 0) {
        edd_over++;
      } else if (patient.edd_alignment < 0) {
        edd_under++;
      }
    });

    const COLORS = ["#00C49F", "#ff7300", "#0088fe"];

    const total = edd_met + edd_over + edd_under;

    const data = [
      {
        name: "EDD Met",
        value: edd_met,
        percentage: (edd_met / total) * 100,
      },
      {
        name: "EDD Over",
        value: edd_over,
        percentage: (edd_over / total) * 100,
      },
      {
        name: "EDD Under",
        value: edd_under,
        percentage: (edd_under / total) * 100,
      },
    ];

    return total > 0 ? (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          flexDirection: "column",
          width: 350,
          height: 300,
        }}
      >
        <Typography
          variant="subtitle2"
          align="center"
          style={{ whiteSpace: "nowrap" }}
        >
          EDD Metrics
        </Typography>
        <PieChart width={350} height={300}>
          <Pie
            dataKey="value"
            data={data}
            cx={"50%"}
            cy={"50%"}
            outerRadius={80}
            fill="#8884d8"
            label={({
              cx,
              cy,
              midAngle,
              innerRadius,
              outerRadius,
              percent,
              value,
              index,
            }) => {
              const RADIAN = Math.PI / 180;

              const radius = 25 + innerRadius + (outerRadius - innerRadius);
              const x = cx + radius * Math.cos(-midAngle * RADIAN);
              const y = cy + radius * Math.sin(-midAngle * RADIAN);

              return (
                <text
                  x={x}
                  y={y}
                  fill={COLORS[index % COLORS.length]}
                  textAnchor={x > cx ? "start" : "end"}
                  dominantBaseline="central"
                >
                  {data[index].percentage.toFixed(0) +
                    "% (" +
                    data[index].value +
                    ")"}
                </text>
              );
            }}
          >
            {data.map((entry, index) => (
              <Cell
                key={`cell-${index}`}
                fill={COLORS[index % COLORS.length]}
              />
            ))}
          </Pie>
          <Legend
            align="center"
            verticalAlign="bottom"
            height={36}
            payload={data.map((item, index) => ({
              id: item.name,
              type: "square",
              value: `${item.name} - ${item.percentage.toFixed(0)}% (${
                item.value
              })`,
              color: COLORS[index % COLORS.length],
            }))}
          />
          <Tooltip />
        </PieChart>
      </div>
    ) : (
      <div></div>
    );
  };

  const generateBedUtilizationGraph = (patientList, name) => {
    const filteredHospital = dataHospitals.find((item) => item.name === name);

    let totalBeds = filteredHospital.beds;

    const fromDateMoment = moment(fromDate);
    const toDateMoment = moment(toDate);

    const daysDiff = toDateMoment.diff(fromDateMoment, "days");

    const bedDaysAvailable = totalBeds * daysDiff;
    const bedDaysUsed = getTotalStayLength(patientList);

    const COLORS = ["#FF5733", "#36A2EB"];

    const total = bedDaysAvailable + bedDaysUsed;

    const data = [
      {
        name: "Bed Days Used",
        value: bedDaysUsed,
        percentage: (bedDaysUsed / total) * 100,
      },
      {
        name: "Bed Days Available",
        value: bedDaysAvailable,
        percentage: (bedDaysAvailable / total) * 100,
      },
    ];

    return total > 0 ? (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          flexDirection: "column",
          width: 365,
          height: 300,
        }}
      >
        <Typography
          variant="subtitle2"
          align="center"
          style={{ whiteSpace: "nowrap" }}
        >
          Capacity Management
        </Typography>
        <PieChart width={365} height={300}>
          <Pie
            dataKey="value"
            data={data}
            cx={"50%"}
            cy={"50%"}
            outerRadius={80}
            fill="#8884d8"
            label={({
              cx,
              cy,
              midAngle,
              innerRadius,
              outerRadius,
              percent,
              value,
              index,
            }) => {
              const RADIAN = Math.PI / 180;

              const radius = 25 + innerRadius + (outerRadius - innerRadius);
              const x = cx + radius * Math.cos(-midAngle * RADIAN);
              const y = cy + radius * Math.sin(-midAngle * RADIAN);

              return (
                <text
                  x={x}
                  y={y}
                  fill={COLORS[index % COLORS.length]}
                  textAnchor={x > cx ? "start" : "end"}
                  dominantBaseline="central"
                >
                  {data[index].percentage.toFixed(0) +
                    "% (" +
                    data[index].value +
                    ")"}
                </text>
              );
            }}
          >
            {data.map((entry, index) => (
              <Cell
                key={`cell-${index}`}
                fill={COLORS[index % COLORS.length]}
              />
            ))}
          </Pie>
          <Legend
            align="center"
            verticalAlign="bottom"
            height={36}
            payload={data.map((item, index) => ({
              id: item.name,
              type: "square",
              value: `${item.name} - ${item.percentage.toFixed(0)}% (${
                item.value
              })`,
              color: COLORS[index % COLORS.length],
            }))}
          />
          <Tooltip />
        </PieChart>
      </div>
    ) : (
      <div></div>
    );
  };

  const generateOccupancyLevelsGraph = (
    patientList,
    name,
    fromDate,
    toDate
  ) => {
    const filteredHospital = dataHospitals.find((item) => item.name === name);

    const fromDateMoment = moment(fromDate);
    const toDateMoment = moment(toDate);

    const dateRange = [];
    const currentDate = fromDateMoment.clone(); // Use clone to avoid mutating original fromDateMoment
    while (currentDate.isSameOrBefore(toDateMoment, "day")) {
      dateRange.push(currentDate.format("YYYY-MM-DD"));
      currentDate.add(1, "day");
    }

    const occupancyLevels = {};

    // Initialize occupancy levels for each date
    dateRange.forEach((date) => {
      let count = 0;

      patientList.forEach((patient) => {
        const admittedDate = moment(patient.admitted_date).format("YYYY-MM-DD");
        const dischargeDate = patient.discharge_date
          ? moment(patient.discharge_date).format("YYYY-MM-DD")
          : null;

        // Check if the date falls within the admission and discharge dates of the patient
        if (
          moment(date).isSameOrAfter(admittedDate, "day") &&
          (dischargeDate === null ||
            moment(date).isBefore(dischargeDate, "day"))
        ) {
          count++;
        }
      });

      occupancyLevels[date] = count;
    });

    const data = dateRange.map((date) => ({
      date: date,
      Occupancy: occupancyLevels[date] || 0,
    }));

    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          flexDirection: "column",
          width: "100%",
          height: 450,
        }}
      >
        <Typography
          variant="subtitle2"
          align="center"
          style={{ whiteSpace: "nowrap" }}
        >
          Occupancy Levels
        </Typography>
        <ResponsiveContainer width="100%" height="100%">
          <ComposedChart
            data={data}
            width={800}
            height={450}
            margin={{
              top: 20,
              right: 50,
              bottom: 20,
              left: 0,
            }}
          >
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis
              dataKey="date"
              //scale="time"
              tickFormatter={(date) => moment(date).format("DD/MM/yyyy")}
            />
            <YAxis type="number" domain={[0, filteredHospital.beds]} />
            <Area
              type="monotone"
              dataKey="Occupancy"
              fill="#8884d8"
              stroke="#8884d8"
            />
            <Tooltip labelFormatter={formatDate} />
            <ReferenceLine y={filteredHospital.beds} label="Max" stroke="red" />
            <Legend />
          </ComposedChart>
        </ResponsiveContainer>
      </div>
    );
  };

  // const generateOccupancyLevelsGraph = (
  //   patientList,
  //   name,
  //   fromDate,
  //   toDate
  // ) => {
  //   const filteredHospital = dataHospitals.find((item) => item.name === name);

  //   const fromDateMoment = moment(fromDate);
  //   const toDateMoment = moment(toDate);

  //   const dateRange = [];
  //   const currentDate = fromDateMoment;
  //   while (currentDate.isSameOrBefore(toDateMoment, "day")) {
  //     dateRange.push(currentDate.format("YYYY-MM-DD"));
  //     currentDate.add(1, "day");
  //   }

  //   const occupancyLevels = {};

  //   patientList.forEach((patient) => {
  //     const admittedDate = moment(patient.admitted_date).format("YYYY-MM-DD");
  //     const dischargeDate = moment(patient.discharge_date).format("YYYY-MM-DD");

  //     if (!occupancyLevels[admittedDate]) {
  //       occupancyLevels[admittedDate] = 0;
  //     }

  //     occupancyLevels[admittedDate]++;

  //     if (dischargeDate !== "Invalid date") {
  //       if (!occupancyLevels[dischargeDate]) {
  //         occupancyLevels[dischargeDate] = 0;
  //       }
  //       occupancyLevels[dischargeDate]--;
  //     }
  //   });

  //   let runningTotal = 0;

  //   dateRange.forEach((date) => {
  //     if (occupancyLevels[date]) {
  //       runningTotal += occupancyLevels[date];
  //     }
  //     occupancyLevels[date] = runningTotal;
  //   });

  //   const data = dateRange.map((date) => ({
  //     date: date,
  //     Occupancy: occupancyLevels[date] || 0,
  //   }));

  //   return (
  //     <div
  //       style={{
  //         display: "flex",
  //         justifyContent: "center",
  //         flexDirection: "column",
  //         width: "100%",
  //         height: 450,
  //       }}
  //     >
  //       <Typography
  //         variant="subtitle2"
  //         align="center"
  //         style={{ whiteSpace: "nowrap" }}
  //       >
  //         Occupancy Levels
  //       </Typography>
  //       <ResponsiveContainer width="100%" height="100%">
  //         <ComposedChart
  //           data={data}
  //           width={800}
  //           height={450}
  //           margin={{
  //             top: 20,
  //             right: 50,
  //             bottom: 20,
  //             left: 0,
  //           }}
  //         >
  //           <CartesianGrid strokeDasharray="3 3" />
  //           <XAxis
  //             dataKey="date"
  //             //scale="time"
  //             tickFormatter={(date) => moment(date).format("DD/MM/yyyy")}
  //           />
  //           <YAxis type="number" domain={[0, filteredHospital.beds]} />
  //           <Area
  //             type="monotone"
  //             dataKey="Occupancy"
  //             fill="#8884d8"
  //             stroke="#8884d8"
  //           />
  //           <Tooltip labelFormatter={formatDate} />
  //           <ReferenceLine y={filteredHospital.beds} label="Max" stroke="red" />

  //           <Legend />
  //         </ComposedChart>
  //       </ResponsiveContainer>
  //     </div>
  //   );
  // };

  const generatePatientStayDurationGraph = (patientList) => {
    const calculateAverageStayDuration = () => {
      const totalStayDuration = patientList.reduce(
        (acc, patient) => acc + patient.stay_length,
        0
      );
      return totalStayDuration / patientList.length;
    };

    const averageStayDuration = calculateAverageStayDuration();

    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          flexDirection: "column",
          width: "100%",
          height: 450,
        }}
      >
        <Typography
          variant="subtitle2"
          align="center"
          style={{ whiteSpace: "nowrap" }}
        >
          Patient Stay Duration
        </Typography>
        <ResponsiveContainer width="100%" height="100%">
          <BarChart
            width={730}
            height={250}
            data={patientList}
            margin={{ top: 20, right: 20, left: 20, bottom: 20 }}
          >
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="name" />
            <YAxis />
            <Tooltip />
            <Legend />
            <Bar
              dataKey="stay_length"
              fill="#8884d895"
              name="Stay Duration"
              label={{ position: "top" }}
            />
            <ReferenceLine
              y={averageStayDuration}
              stroke="red"
              label={{
                position: "top",
                value: `Average: ${averageStayDuration.toFixed(2)} days`,
                fill: "red",
                fontSize: 14,
              }}
            />
          </BarChart>
        </ResponsiveContainer>
      </div>
    );
  };

  const generatePatientAverageAgeGraph = (patientList) => {
    const calculateAverageAge = () => {
      const totalAge = patientList.reduce((acc, patient) => {
        const dobString = patient.dob;
        const dobMoment = moment(dobString, "YYYY-MM-DD");
        const age = moment().diff(dobMoment, "years");
        return acc + age;
      }, 0);
      return totalAge / patientList.length;
    };

    const averageAge = calculateAverageAge();

    const ageData = patientList.map((patient) => {
      const dobString = patient.dob;
      const dobMoment = moment(dobString, "YYYY-MM-DD");
      const age = moment().diff(dobMoment, "years");
      return { name: patient.name, age };
    });

    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          flexDirection: "column",
          width: "100%",
          height: 450,
        }}
      >
        <Typography
          variant="subtitle2"
          align="center"
          style={{ whiteSpace: "nowrap" }}
        >
          Patient Average Age
        </Typography>
        <ResponsiveContainer width="100%" height="100%">
          <BarChart
            width={730}
            height={250}
            data={ageData}
            margin={{ top: 20, right: 20, left: 20, bottom: 20 }}
          >
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="name" />
            <YAxis />
            <Tooltip />
            <Legend />

            <Bar
              dataKey="age"
              fill="#8884D495"
              name="Age"
              label={{ position: "top" }}
            />

            <ReferenceLine
              y={averageAge}
              stroke="red"
              label={{
                position: "top",
                value: `Average: ${averageAge.toFixed(2)} years`,
                fill: "red",
                fontSize: 14,
              }}
            />
          </BarChart>
        </ResponsiveContainer>
      </div>
    );
  };

  const formatDate = (date) => {
    const options = { day: "2-digit", month: "2-digit", year: "numeric" };
    return new Intl.DateTimeFormat("en-GB", options).format(new Date(date));
  };

  const formatLabelDate = (value) =>
    new Date(value).toLocaleString("en-GB", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });

  const getTotalStayLength = (patientList) => {
    let totalStayLength = 0;

    patientList.forEach((row) => {
      const stayLength = parseInt(row.stay_length) || 0;
      totalStayLength += stayLength;
    });
    return totalStayLength;
  };

  const getTotalEddAlignment = (patientList) => {
    let totalEddAlignment = 0;

    patientList.forEach((row) => {
      const eddAlignment = parseInt(row.edd_alignment) || 0;
      totalEddAlignment += eddAlignment;
    });
    return totalEddAlignment;
  };

  const renderSummaryData = (name, patientList) => {
    const filteredHospital = dataHospitals.find((item) => item.name === name);

    if (!filteredHospital) {
      return null;
    }

    let admitted = 0;
    let discharged = 0;

    patientList.forEach((patient) => {
      if (patient.status === 1) {
        discharged++;
      } else if (patient.status === 0) {
        admitted++;
      }
    });

    return (
      <Grid
        container
        spacing={2}
        style={{
          padding: 10,
          display: "flex",
        }}
      >
        <Grid item xs={1}>
          <Typography
            variant="subtitle2"
            align="center"
            style={{ whiteSpace: "nowrap" }}
          >
            Active Patients
          </Typography>
          <Typography variant="h6" align="center">
            {admitted}
          </Typography>
        </Grid>
        <Grid item xs={1}>
          <Typography
            variant="subtitle2"
            align="center"
            style={{ whiteSpace: "nowrap" }}
          >
            Discharged Patients
          </Typography>
          <Typography variant="h6" align="center">
            {discharged}
          </Typography>
        </Grid>
      </Grid>
    );
  };

  const renderRejectedAdmissionsData = (patientList) => {
    const filteredAdmissionRejectionPatients = patientList.filter(
      (patient) => patient.admission_rejection === 1
    );

    return (
      <div
        style={{
          display: "flex",
          flexDirection: "column",
        }}
      >
        <Typography
          variant="subtitle2"
          align="center"
          style={{ whiteSpace: "nowrap" }}
        >
          Rejected Admissions Details
        </Typography>

        <TableContainer>
          <Table size="small" aria-label="a dense table">
            <TableHead>
              <TableRow>
                <TableCell key={"mrn"} align="left">
                  MRN
                </TableCell>
                <TableCell key={"admission_rejection"} align="left">
                  Reason for Rejection
                </TableCell>
                <TableCell key={"discharge_date"} align="left">
                  Date of rejection
                </TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {filteredAdmissionRejectionPatients.map((row, index) => (
                <TableRow key={index}>
                  <TableCell align="left">{row.mrn}</TableCell>
                  <TableCell align="left">
                    {row.admission_decline === "Other"
                      ? row.admission_decline_other
                      : row.admission_decline}
                  </TableCell>
                  <TableCell align="left">
                    {row.discharge_date !== null
                      ? moment(row.discharge_date).format("DD/MM/YYYY")
                      : "-"}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </div>
    );
  };

  const renderRejectedAdmissionsGraph = (patientList) => {
    let activeCount = 0;
    let dischargedCount = 0;
    let dischargedRejectedCount = 0;

    patientList.forEach((patient) => {
      if (patient.status === 0) {
        activeCount++;
      } else if (patient.status === 1 && patient.admission_rejection === 0) {
        dischargedCount++;
      } else if (patient.status === 1 && patient.admission_rejection === 1) {
        dischargedRejectedCount++;
      }
    });

    const COLORS = ["#11cb5f", "#c70039", "#6d4f7d"];

    const totalPatients =
      activeCount + dischargedCount + dischargedRejectedCount;

    const data = [
      {
        name: "Active",
        value: activeCount,
        percentage: (activeCount / totalPatients) * 100,
      },
      {
        name: "Discharged",
        value: dischargedCount,
        percentage: (dischargedCount / totalPatients) * 100,
      },
      {
        name: "Discharged Rejected",
        value: dischargedRejectedCount,
        percentage: (dischargedRejectedCount / totalPatients) * 100,
      },
    ];

    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          flexDirection: "column",
          width: 350,
          height: 300,
        }}
      >
        <Typography
          variant="subtitle2"
          align="center"
          style={{ whiteSpace: "nowrap" }}
        >
          {"Rejected Admissions Overview (" + totalPatients + ")"}
        </Typography>
        <PieChart width={350} height={300}>
          <Pie
            dataKey="value"
            data={data}
            cx={"50%"}
            cy={"50%"}
            outerRadius={80}
            fill="#8884d8"
            label={({
              cx,
              cy,
              midAngle,
              innerRadius,
              outerRadius,
              percent,
              value,
              index,
            }) => {
              const RADIAN = Math.PI / 180;

              const radius = 25 + innerRadius + (outerRadius - innerRadius);
              const x = cx + radius * Math.cos(-midAngle * RADIAN);
              const y = cy + radius * Math.sin(-midAngle * RADIAN);

              return (
                <text
                  x={x}
                  y={y}
                  fill={COLORS[index % COLORS.length]}
                  textAnchor={x > cx ? "start" : "end"}
                  dominantBaseline="central"
                >
                  {data[index].percentage.toFixed(0) +
                    "% (" +
                    data[index].value +
                    ")"}
                </text>
              );
            }}
          >
            {data.map((entry, index) => (
              <Cell
                key={`cell-${index}`}
                fill={COLORS[index % COLORS.length]}
              />
            ))}
          </Pie>
          <Legend
            align="center"
            verticalAlign="bottom"
            height={36}
            payload={data.map((item, index) => ({
              id: item.name,
              type: "square",
              value: `${item.name} - ${item.percentage.toFixed(0)}% (${
                item.value
              })`,
              color: COLORS[index % COLORS.length],
            }))}
          />

          <Tooltip />
        </PieChart>
      </div>
    );
  };

  return (
    <Grid
      container
      spacing={1}
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      {data !== null && data.length > 0 ? (
        <>
          {data.map((dataItem) => (
            <Grid
              item
              xs={12}
              md={12}
              style={{ margin: 10, pageBreakAfter: "always" }}
            >
              <Typography variant="h6" style={{}}>
                {dataItem.hospital_name}
              </Typography>
              <Divider
                orientation="horizontal"
                variant="fullWidth"
                style={{ padding: 2, marginBottom: 15 }}
              />

              {renderSummaryData(dataItem.hospital_name, dataItem.patients)}

              <Divider />

              <TableContainer>
                <Table size="small" aria-label="a dense table">
                  <TableHead>
                    <TableRow>
                      <TableCell key={"mrn"} align="left">
                        MRN
                      </TableCell>
                      <TableCell key={"admitted_date"} align="left">
                        Admitted Date
                      </TableCell>
                      <TableCell key={"discharge_date"} align="left">
                        Discharge Date
                      </TableCell>
                      <TableCell key={"exptected_discharge_date"} align="left">
                        EDD
                      </TableCell>
                      <TableCell key={"stay_length"} align="left">
                        Stay Length
                      </TableCell>
                      <TableCell key={"edd_alignment"} align="left">
                        EDD Alignment
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {dataItem.patients.map((row, index) => (
                      <TableRow key={index}>
                        <TableCell align="left">{row.mrn}</TableCell>
                        <TableCell align="left">
                          {row.admitted_date !== null
                            ? moment(row.admitted_date).format("DD/MM/YYYY")
                            : "-"}
                        </TableCell>
                        <TableCell align="left">
                          {row.discharge_date !== null
                            ? moment(row.discharge_date).format("DD/MM/YYYY")
                            : "-"}
                        </TableCell>
                        <TableCell align="left">
                          {row.exptected_discharge_date !== null
                            ? moment(row.exptected_discharge_date).format(
                                "DD/MM/YYYY"
                              )
                            : "-"}
                        </TableCell>
                        <TableCell align="left">{row.stay_length}</TableCell>
                        <TableCell align="left">{row.edd_alignment}</TableCell>
                      </TableRow>
                    ))}
                    <TableRow>
                      <TableCell colSpan={3} />
                      <TableCell colSpan={1}>
                        <span style={{ fontWeight: "bold" }}>Total</span>
                      </TableCell>
                      <TableCell align="left">
                        <span style={{ fontWeight: "bold" }}>
                          {getTotalStayLength(dataItem.patients)}
                        </span>
                      </TableCell>
                      <TableCell align="left">
                        <span style={{ fontWeight: "bold" }}>
                          {getTotalEddAlignment(dataItem.patients)}
                        </span>
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>

              <div
                style={{
                  display: "flex",
                  flexDirection: "row",
                  justifyContent: "space-between",
                  paddingTop: 40,
                  pageBreakBefore: "always",
                }}
              >
                {generateGenderGraph(dataItem.patients)}
                {generateStatusGraph(dataItem.patients)}
                {generateEDDMetricsGraph(dataItem.patients)}
                {generateBedUtilizationGraph(
                  dataItem.patients,
                  dataItem.hospital_name
                )}
              </div>

              <div
                style={{
                  display: "flex",
                  flexDirection: "row",
                  justifyContent: "space-between",
                  paddingTop: 40,
                  pageBreakBefore: "always",
                }}
              >
                {generateOccupancyLevelsGraph(
                  dataItem.patients,
                  dataItem.hospital_name,
                  fromDate,
                  toDate
                )}
              </div>
              <div
                style={{
                  display: "flex",
                  flexDirection: "row",
                  justifyContent: "space-around",
                  paddingTop: 40,
                  pageBreakBefore: "always",
                }}
              >
                {renderRejectedAdmissionsData(dataItem.patients)}
                {renderRejectedAdmissionsGraph(dataItem.patients)}
              </div>
              <div
                style={{
                  display: "flex",
                  flexDirection: "row",
                  justifyContent: "space-between",
                  paddingTop: 40,
                  pageBreakBefore: "always",
                }}
              >
                {generatePatientStayDurationGraph(dataItem.patients)}
              </div>
              <div
                style={{
                  display: "flex",
                  flexDirection: "row",
                  justifyContent: "space-between",
                  paddingTop: 40,
                  pageBreakBefore: "always",
                }}
              >
                {generatePatientAverageAgeGraph(dataItem.patients)}
              </div>
            </Grid>
          ))}
        </>
      ) : (
        data === null && (
          <Typography
            data-test={"reports-empty-msg"}
            style={{ textAlign: "center" }}
          >
            There is no data available for the selected ranges.
          </Typography>
        )
      )}
    </Grid>
  );
}
